import { v4 } from 'uuid';
import {
  ExtendedTrackingReference,
  RequiredTrackingProps,
  TrackedOrderDetails,
  TrackingAppLocation,
  TrackingEventName,
  TrackingReference,
  UserDetails,
} from './types';
import { cloneDeep } from 'lodash';
import { OrderObjectId, OrderSelection } from '~/types/Order';

class TrackingHelper {
  appVersion: 'classic' | 'blackbeard' = 'classic';
  // The order object ID when only a single order is selected
  orderId?: OrderObjectId;
  orderIds?: OrderObjectId[];
  userDetails?: UserDetails;
  trackedOrderDetailsByShipment?: Record<number, TrackedOrderDetails>;
  selectedOrders?: OrderSelection;
  isBulk = false;

  // We get this from the FullStoryMonitor because it's the only way to make a globally available object
  fullStoryEvent?: (
    eventName: string,
    eventProperties: {
      [key: string]: unknown;
    },
  ) => void;

  private properties() {
    return {
      appVersionName: this.appVersion,
      isBulk: this.isBulk,
      orderId: this.orderId,
      orderIds: this.orderIds,
      userDetails: this.userDetails,
      trackedOrderDetailsByShipment: this.trackedOrderDetailsByShipment,
      selectedOrders: this.selectedOrders,
      // NOTE: This says "order", it's counting orders, but it does not take Batch Entries into account.
      // This count might be incorrect if split orders are selected.
      orderQuantity: this.orderIds?.length || (this.orderId ? 1 : 0),
    } satisfies Partial<TrackingReference>;
  }

  private getReferenceId() {
    return v4();
  }

  private getBaseTrackingReference<E extends TrackingEventName>(
    initialEventName: E,
    appLocation: TrackingAppLocation,
    // TODO: Some events have additional required properties. It would be convenient to stash them in the trackingReference
    // Should we extend the TrackingReference type to include a generic for additional properties?
  ): TrackingReference & { initialEventName: E } {
    return {
      initialEventName,
      referenceId: this.getReferenceId(),
      appLocation,
      ...cloneDeep(this.properties()),
    };
  }

  private extendTrackingReference<E extends TrackingEventName>(
    trackingReference: TrackingReference & { initialEventName: E },
    requiredProps: RequiredTrackingProps[E],
  ): ExtendedTrackingReference<E> {
    return {
      ...cloneDeep(trackingReference),
      ...cloneDeep(requiredProps),
    };
  }

  appendGlobalProps<T extends object>(input?: T) {
    return {
      ...input,
      ...cloneDeep(this.properties()),
    };
  }

  getTrackingReference<E extends TrackingEventName>(
    initialEventName: E,
    appLocation: TrackingAppLocation,
    requiredProps: RequiredTrackingProps[E],
  ): ExtendedTrackingReference<E> {
    const trackingReference = this.getBaseTrackingReference(initialEventName, appLocation);
    return this.extendTrackingReference(trackingReference, requiredProps);
  }

  amendTrackingReferenceOrders<T extends TrackingReference>(
    trackingReference: T,
    orderIds?: string[],
  ): T {
    const duped = cloneDeep(trackingReference);
    duped.orderId = undefined;
    duped.orderIds = undefined;
    duped.orderQuantity = undefined;
    duped.isBulk = false;

    if (!orderIds) {
      return duped;
    }

    switch (orderIds.length) {
      case 0: {
        return duped;
      }
      case 1: {
        return {
          ...duped,
          orderId: orderIds[0],
          orderQuantity: 1,
        };
      }
      default: {
        return {
          ...duped,
          orderIds,
          orderQuantity: orderIds.length,
          isBulk: true,
        };
      }
    }
  }

  amendTrackingReferenceTrackedOrderDetailsByShipment<T extends TrackingReference>(
    trackingReference: T,
    trackedOrderDetailsByShipment: Record<number, TrackedOrderDetails>,
  ): T {
    return {
      ...cloneDeep(trackingReference),
      trackedOrderDetailsByShipment,
    };
  }

  amendTrackingReferenceOrderSelection<T extends TrackingReference>(
    trackingReference: T,
    selectedOrders?: OrderSelection,
  ): T {
    if (!selectedOrders) {
      return trackingReference;
    }
    const orderIds = Object.keys(selectedOrders);
    const trackingReferenceWithOrderIds = trackingHelper.amendTrackingReferenceOrders(
      trackingReference,
      orderIds,
    );
    trackingReferenceWithOrderIds.selectedOrders = selectedOrders;
    return trackingReferenceWithOrderIds;
  }
}

// Export a singleton TrackingHelper
export const trackingHelper = new TrackingHelper();
